import { useState } from 'react'
import PropTypes from 'prop-types'
import { cloneDeep } from 'lodash'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { useTranslation } from 'react-i18next'

import { FORM_DATA_INPUT, reorder, validateDataInputs } from '../../helpers'
import ModalContainer from '../../../../../components/ModalContainer/ModelContainer'
import Icon from '../../../../../components/Icons'
import { generateId } from '../../../../../utils'
import DataInput from '../components/DataInput'

const MAX_INPUTS_COUNT = 20

const SortableDataInput = ({ id, index, children }) => {
  const { ref } = useSortable({ id, index })
  return (
    <div
      ref={ref}
      className="checklist-modal-input-row"
      style={{ left: 'auto !important', marginBottom: 12 }}
    >
      {children}
    </div>
  )
}

const SetFormStructureModal = ({ onClose = () => {}, onSubmit = () => {}, formState = [] }) => {
  const { t } = useTranslation(['checklists-create-task'])
  const [dataInputs, setDataInputs] = useState(formState)

  const onFieldChange = (index, field, value) => {
    const newInputs = cloneDeep(dataInputs)
    newInputs[index] = { ...newInputs[index], [field]: value }
    setDataInputs(newInputs)
  }

  const onAddDataInput = () => {
    const newInputs = cloneDeep(dataInputs)
    newInputs.push({ ...FORM_DATA_INPUT, _id: generateId() })
    setDataInputs(newInputs)
  }

  const onRemoveDataInput = (index) => {
    const newInputs = cloneDeep(dataInputs)
    newInputs.splice(index, 1)
    setDataInputs(newInputs)
  }

  const onCopyDataInput = (index) => {
    if (dataInputs.length >= MAX_INPUTS_COUNT) return
    const newInputs = cloneDeep(dataInputs)
    newInputs.splice(index, 0, { ...newInputs[index], _id: generateId() })
    setDataInputs(newInputs)
  }

  const onAddOption = (index) => {
    const newInputs = cloneDeep(dataInputs)
    newInputs[index].options.push('')
    setDataInputs(newInputs)
  }

  const onRemoveOption = (index, optionIndex) => {
    const newInputs = cloneDeep(dataInputs)
    newInputs[index].options.splice(optionIndex, 1)
    setDataInputs(newInputs)
  }

  const onChangeOption = (index, optionIndex, value) => {
    const newInputs = cloneDeep(dataInputs)
    newInputs[index].options[optionIndex] = value
    setDataInputs(newInputs)
  }

  const onDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return
    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return
    const updatedInputs = reorder([...dataInputs], sourceIndex, targetIndex)
    setDataInputs(updatedInputs)
  }

  const onSetFormStructure = () => {
    const { isValid, newInputs } = validateDataInputs(dataInputs, t)
    setDataInputs(newInputs)
    if (isValid) {
      onSubmit(newInputs)
    }
  }

  return (
    <ModalContainer
      additionalClass="checklist-modal"
      onClose={onClose}
      headerTitle={t('checklists-create-task:dataInput.setFormStructureButton')}
    >
      <div className="checklist-modal-content checklist-task-data">
        <DragDropProvider onDragEnd={onDragEnd}>
          <div style={{ width: '100%' }}>
            {dataInputs.map((dataInput, index) => (
              <SortableDataInput key={dataInput._id} id={dataInput._id} index={index}>
                <DataInput
                  onFieldChange={(field, value) => onFieldChange(index, field, value)}
                  onRemove={() => onRemoveDataInput(index)}
                  onCopy={() => onCopyDataInput(index)}
                  onAddOption={() => onAddOption(index)}
                  onRemoveOption={(optionIndex) => onRemoveOption(index, optionIndex)}
                  onChangeOption={(optionIndex, value) => onChangeOption(index, optionIndex, value)}
                  dataInputState={dataInput}
                  dataInputIndex={index}
                  excludeForm
                />
              </SortableDataInput>
            ))}
          </div>
        </DragDropProvider>
        <div className="checklist-modal-input-row">
          <button
            className="button button-black-outline"
            onClick={onAddDataInput}
            style={{ marginRight: 8 }}
            disabled={dataInputs.length >= MAX_INPUTS_COUNT}
          >
            <Icon icon="downLeftLayout" />
            {t('checklists-create-task:dataInput.addDataInputButton')}
          </button>
        </div>
      </div>
      <div className="checklist-modal-actions">
        <button
          className="button button-primary"
          disabled={!dataInputs.length}
          onClick={onSetFormStructure}
        >
          {t('checklists-create-task:dataInput.setFormStructureButton')}
        </button>
      </div>
    </ModalContainer>
  )
}

SetFormStructureModal.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  formState: PropTypes.array,
}

export default SetFormStructureModal
